import { createAsyncThunk } from "@reduxjs/toolkit";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../lib/firebase/functions";
import { RootState } from "../store";
import { IFamily, IWorker } from "../../types/filterFields";
import { updateKanbanCard } from "../slices/kanban";
import { client } from "../../lib/axios";
import { reportError } from "../../lib/errorReporting";

export const getWorkers = createAsyncThunk(
  "databaseTable/getWorkers",
  async (
    {
      currentPage: page,
      order,
      orderField,
      provinceUserSites,
    }: {
      currentPage: number;
      order: "asc" | "desc";
      orderField: string;
      provinceUserSites: string[];
    },
    thunkAPI
  ): Promise<{ count: number; rows: IWorker[]; totalCount?: number }> => {
    const state = thunkAPI.getState() as RootState;
    const filters = state.databaseTable.queryFilters;
    const sedeFilter = state.databaseTable.sedeFilter;

    const _filters = filters
      ?.map((f: any) => {
        let value = Object.values(f)[0];
        let key = Object.keys(f)[0];

        if (Array.isArray(value)) {
          value = value.map(encodeURIComponent).join(",");
        } else {
          value = encodeURIComponent(value as string);
        }

        return `${key}:${value}`;
      })
      .join(";");

    const response = await client.get("/database/workers", {
      params: {
        page,
        order,
        orderField,
        filters: _filters?.length ? _filters : undefined,
        provinceUserSites:
          provinceUserSites?.length > 0
            ? provinceUserSites.join(",")
            : undefined,
        ...(sedeFilter?.length > 0 ? { sede: sedeFilter.join(",") } : {}),
      },
    });

    return response.data as { count: number; rows: IWorker[] };
  }
);

export const getFamilies = createAsyncThunk(
  "databaseTable/getFamilies",
  async (
    {
      currentPage: page,
      order,
      orderField,
      provinceUserSites,
    }: {
      currentPage: number;
      order: string;
      orderField: string;
      provinceUserSites: string[];
    },
    thunkAPI
  ): Promise<{ count: number; rows: IFamily[] }> => {
    const state = thunkAPI.getState() as RootState;
    const sedeFilter = state.databaseTable.sedeFilter;

    const response = await client.get("/database/families", {
      params: {
        page,
        order,
        orderField,
        provinceUserSites:
          provinceUserSites?.length > 0
            ? provinceUserSites.join(",")
            : undefined,
        ...(sedeFilter?.length > 0 ? { sede: sedeFilter.join(",") } : {}),
      },
    });

    return response.data as { count: number; rows: IFamily[] };
  }
);

export const searchWorkers = createAsyncThunk(
  "databaseTable/searchWorkers",
  async (_, thunkAPI): Promise<{ count: number; rows: IWorker[] }> => {
    const state = thunkAPI.getState() as RootState;
    let filters = [...(state.databaseTable.queryFilters ?? ([] as any))];

    if (!filters.some((f: any) => "text_search" in f)) {
      filters.push({ text_search: "" });
    }

    const _filters = filters
      ?.map((f: any) => {
        let value = Object.values(f)[0];
        let key = Object.keys(f)[0];

        if (Array.isArray(value)) {
          value = value.map(encodeURIComponent).join(",");
        } else {
          value = encodeURIComponent(value as string);
        }

        return `${key}:${value}`;
      })
      .join(";");

    const provinceUserSites = state.databaseTable.provinceUserSites;
    const sedeFilter = state.databaseTable.sedeFilter;

    const response = await client.get("/database/workers/search", {
      params: {
        filters: _filters,
        provinceUserSites:
          provinceUserSites?.length > 0
            ? provinceUserSites.join(",")
            : undefined,
        ...(sedeFilter?.length > 0 ? { sede: sedeFilter.join(",") } : {}),
      },
    });

    return response.data as { count: number; rows: IWorker[] };
  }
);

export const searchFamilies = createAsyncThunk(
  "databaseTable/searchFamilies",
  async (_, thunkAPI): Promise<IFamily[]> => {
    const state = thunkAPI.getState() as RootState;
    let filters = [...(state.databaseTable.queryFilters ?? ([] as any))];

    if (!filters.some((f: any) => "text_search" in f)) {
      filters.push({ text_search: "" });
    }

    const _filters = filters
      ?.map((f: any) => {
        let value = Object.values(f)[0];
        let key = Object.keys(f)[0];

        if (Array.isArray(value)) {
          value = value.map(encodeURIComponent).join(",");
        } else {
          value = encodeURIComponent(value as string);
        }

        return `${key}:${value}`;
      })
      .join(";");

    const provinceUserSites = state.databaseTable.provinceUserSites;
    const sedeFilter = state.databaseTable.sedeFilter;

    const response = await client.get("/database/families/search", {
      params: {
        filters: _filters,
        provinceUserSites:
          provinceUserSites?.length > 0
            ? provinceUserSites.join(",")
            : undefined,
        ...(sedeFilter?.length > 0 ? { sede: sedeFilter.join(",") } : {}),
      },
    });

    return response.data as IFamily[];
  }
);

export const editSelected = createAsyncThunk(
  "databaseTable/editSelected",
  async (
    type: string,
    thunkAPI
  ): Promise<{ success: boolean; updatedData: IWorker | IFamily }> => {
    try {
      const state = thunkAPI.getState() as RootState;
      const dispatch = thunkAPI.dispatch;
      const selected = state.databaseTable.selected;

      if (selected) {
        const serializedObject: any = { ...selected };
        // Object.entries(selected).forEach(([key, value]) => {
        //   if (key === "experiences") {
        //   }
        // });
        serializedObject["codice_fiscale"] = selected["codice_fiscale"];
        const res = await client.patch("/database/workers", {
          selected: serializedObject,
          type,
        });
        const result = res.data as {
          success: boolean;
          updatedData: IWorker | IFamily;
        };
        dispatch(updateKanbanCard(result.updatedData));
        return result;
      }

      throw new Error("No worker selected");
    } catch (e) {
      await reportError(e, { operation: "database.selection.edit" });
      throw e;
    }
  }
);
